import {uniq} from 'lodash-es';
import type {AxiosRequestConfig} from 'axios';
import {spotheroGet} from 'api';
import dayjs from 'utils/dayjs';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';
import {sendSentryException} from 'utils/error-handling';
import {EventTypeOption, EventVenue} from 'api/events/events.types';
import {apiDateFormat} from 'hooks/config/index';
import type {RawApiResponse} from 'api/api.types';

/**
 * The request data for getting employee parking event filter options.
 */
export interface GetEmployeeParkingEventFilterOptionsRequest {
    /** The city IDs to fetch filter options for. */
    cityIds: number[];
    /** The date to start fetching filter options from. */
    fromDate: dayjs.Dayjs | string | null;
    /** The date to stop fetching filter options from. */
    toDate: dayjs.Dayjs | string | null;
    /** Additional Axios configuration options for the request. */
    config?: AxiosRequestConfig;
}

/**
 * The formatted response data for getting employee parking event filter options.
 */
export interface GetEmployeeParkingEventFilterOptionsResponse {
    /** The event type options available for filtering. */
    eventTypes: EventTypeOption[];
    /** The venue options available for filtering. */
    venues: EventVenue[];
}

/**
 * The raw response data structure from the API.
 */
type RawEmployeeParkingEventFilterOptionsResponse =
    RawApiResponse<GetEmployeeParkingEventFilterOptionsResponse>;

/**
 * Format the response of the Employee Parking Event Filter Options.
 */
const formatEmployeeParkingEventFilterOptionsResponse = (
    data: RawEmployeeParkingEventFilterOptionsResponse
): GetEmployeeParkingEventFilterOptionsResponse => {
    const camelizedData = camelizeKeys(data) as {
        venues: EventVenue[];
        eventTypes: EventTypeOption[];
    };

    return {
        eventTypes: camelizedData.eventTypes,
        venues: camelizedData.venues,
    };
};

/**
 * Format the request to get Employee Parking Event Filter Options.
 */
const formatEmployeeParkingEventFilterOptionsRequest = (
    data: GetEmployeeParkingEventFilterOptionsRequest
) => {
    const request = {
        city: uniq(data.cityIds),
        fromDate: data.fromDate && dayjs(data.fromDate).format(apiDateFormat),
        toDate: data.toDate && dayjs(data.toDate).format(apiDateFormat),
    };

    const formattedData = convertKeysToSnakeCase(request);

    return formattedData;
};

/**
 * Gets the combined event filter options (event types and venues) for given cities and date range.
 * This endpoint combines the data from event-type-options and destinations endpoints.
 */
const getEmployeeParkingEventFilterOptions = async ({
    cityIds,
    fromDate,
    toDate,
    config = {},
}: GetEmployeeParkingEventFilterOptionsRequest) => {
    try {
        const formattedRequest = formatEmployeeParkingEventFilterOptionsRequest(
            {
                fromDate,
                toDate,
                cityIds,
            }
        );

        const response =
            await spotheroGet<RawEmployeeParkingEventFilterOptionsResponse>(
                `events/by-cities/filters/`,
                formattedRequest,
                {
                    paramsSerializer: {
                        indexes: null,
                    },
                    ...config,
                }
            );

        const formattedResponse =
            formatEmployeeParkingEventFilterOptionsResponse(response.data.data);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

export default getEmployeeParkingEventFilterOptions;
